import React, { useEffect, useMemo, useState } from "react";
import { Pressable, ScrollView, StyleSheet, View } from "react-native";
import {
  ArrowSquareOutIcon,
  CaretDownIcon,
  CaretUpIcon,
  ClipboardTextIcon,
  ClockIcon,
  UploadSimpleIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";

import Pill from "../../../../../components/common/Pill/Pill";
import SortingDropdown from "../../../../../components/common/TableControls/SortingDropdown/SortingDropdown";
import CustomButton from "../../../../../components/common/general/CustomButton/CustomButton";
import CustomProgressBar from "../../../../../components/common/general/CustomProgressBar/CustomProgressBar";
import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { colors, radii, shadowStyles, spacing } from "../../../../../styles/design-system";
import { formatSubmittedDate } from "../../../../../utils/date";
import { formatDurationBetween } from "../../../../../utils/timeUtils";

import type {
  SubmissionStatusFilter,
  SubmissionTableRow,
  SubmissionTypeFilter,
} from "../RosterInsightGradesAndSubmissions.utils";
import {
  getDisplayNumber,
  getStatusPillColors,
  getSubmissionTypeColors,
  getValueColor,
  SUBMISSION_STATUS_OPTIONS,
  SUBMISSION_TYPE_OPTIONS,
} from "../RosterInsightGradesAndSubmissions.utils";

interface RosterInsightSubmissionsTableProps {
  submissionRows: SubmissionTableRow[];
  onViewSubmission: (submissionId: string) => void;
}

const RosterInsightSubmissionsTable = ({ submissionRows, onViewSubmission }: RosterInsightSubmissionsTableProps) => {
  const [submissionTypeFilter, setSubmissionTypeFilter] = useState<SubmissionTypeFilter>("All");
  const [submissionTypeDropdownOpen, setSubmissionTypeDropdownOpen] = useState(false);
  const [submissionStatusFilter, setSubmissionStatusFilter] = useState<SubmissionStatusFilter>("All");
  const [submissionStatusDropdownOpen, setSubmissionStatusDropdownOpen] = useState(false);
  const [expandedSubmissionId, setExpandedSubmissionId] = useState<string | null>(null);

  const handleSubmissionTypeFilterChange: React.Dispatch<React.SetStateAction<string>> = (value) => {
    const nextValue = typeof value === "function" ? value(submissionTypeFilter) : value;
    setSubmissionTypeFilter(nextValue as SubmissionTypeFilter);
  };

  const handleSubmissionStatusFilterChange: React.Dispatch<React.SetStateAction<string>> = (value) => {
    const nextValue = typeof value === "function" ? value(submissionStatusFilter) : value;
    setSubmissionStatusFilter(nextValue as SubmissionStatusFilter);
  };

  const filteredSubmissionRows = useMemo(
    () =>
      submissionRows.filter((row) => {
        if (submissionTypeFilter !== "All" && row.submission.submissionType !== submissionTypeFilter) {
          return false;
        }

        if (submissionStatusFilter === "All") {
          return true;
        }

        if (submissionStatusFilter === "Late") {
          return row.isLate;
        }

        return row.normalizedStatus === submissionStatusFilter;
      }),
    [submissionRows, submissionStatusFilter, submissionTypeFilter]
  );

  useEffect(() => {
    if (filteredSubmissionRows.length === 0) {
      setExpandedSubmissionId(null);
      return;
    }

    setExpandedSubmissionId((currentExpandedSubmissionId) => {
      return currentExpandedSubmissionId &&
        filteredSubmissionRows.some((row) => row.submission.id === currentExpandedSubmissionId)
        ? currentExpandedSubmissionId
        : null;
    });
  }, [filteredSubmissionRows]);

  return (
    <View style={[styles.submissionsCard, shadowStyles.sm]}>
      <View style={styles.submissionsHeader}>
        <CustomText text="Submissions" size="m" weight="bold" useTranslationText={false} style={styles.cardTitle} />

        <View style={styles.submissionsFilters}>
          <SortingDropdown
            sortBy={submissionTypeFilter}
            setSortBy={handleSubmissionTypeFilterChange}
            sortDropdownOpen={submissionTypeDropdownOpen}
            setSortDropdownOpen={setSubmissionTypeDropdownOpen}
            sortItems={SUBMISSION_TYPE_OPTIONS}
            accessibilityLabel="Filter submissions by type"
          />
          <SortingDropdown
            sortBy={submissionStatusFilter}
            setSortBy={handleSubmissionStatusFilterChange}
            sortDropdownOpen={submissionStatusDropdownOpen}
            setSortDropdownOpen={setSubmissionStatusDropdownOpen}
            sortItems={SUBMISSION_STATUS_OPTIONS}
            accessibilityLabel="Filter submissions by status"
          />
        </View>
      </View>

      <View style={styles.tableViewport}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.tableScroll}
          contentContainerStyle={styles.tableScrollContent}>
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <View style={[styles.tableCell, styles.assignmentColumn]}>
                <CustomText
                  text="Assignment"
                  size="s"
                  weight="medium"
                  useTranslationText={false}
                  style={styles.headerText}
                />
              </View>
              <View style={[styles.tableCell, styles.moduleColumn]}>
                <CustomText
                  text="Module"
                  size="s"
                  weight="medium"
                  useTranslationText={false}
                  style={styles.headerText}
                />
              </View>
              <View style={[styles.tableCell, styles.submittedColumn]}>
                <CustomText
                  text="Submitted"
                  size="s"
                  weight="medium"
                  useTranslationText={false}
                  style={styles.headerText}
                />
              </View>
              <View style={[styles.tableCell, styles.statusColumn]}>
                <CustomText
                  text="Status"
                  size="s"
                  weight="medium"
                  useTranslationText={false}
                  style={styles.headerText}
                />
              </View>
              <View style={[styles.tableCell, styles.gradeColumn]}>
                <CustomText
                  text="Grade"
                  size="s"
                  weight="medium"
                  useTranslationText={false}
                  style={styles.headerText}
                />
              </View>
              <View style={[styles.tableCell, styles.attemptsColumn]}>
                <CustomText
                  text="Attempts"
                  size="s"
                  weight="medium"
                  useTranslationText={false}
                  style={styles.headerText}
                />
              </View>
              <View style={[styles.tableCell, styles.expandColumn]} />
            </View>

            {filteredSubmissionRows.length === 0 ? (
              <View style={styles.emptyState}>
                <CustomText
                  text="No submissions match the selected filters."
                  size="s"
                  useTranslationText={false}
                  style={styles.emptyStateText}
                />
              </View>
            ) : (
              filteredSubmissionRows.map((row, index) => {
                const { submission } = row;
                const isExpanded = submission.id === expandedSubmissionId;
                const statusPillColors = getStatusPillColors(row.normalizedStatus);
                const typeColors = getSubmissionTypeColors();
                const hasScore =
                  row.scorePercentage != null && submission.grade != null && submission.maximumPoints != null;
                const durationText =
                  submission.submittedOn && submission.created
                    ? formatDurationBetween(submission.created, submission.submittedOn)
                    : "N/A";
                const dueDateText = formatSubmittedDate(submission.dueDate);
                const submittedDateText =
                  submission.status === "Missing"
                    ? "Not submitted"
                    : submission.submittedOn
                      ? formatSubmittedDate(submission.submittedOn)
                      : "Not yet";

                return (
                  <View
                    key={submission.id}
                    style={[
                      styles.tableRowWrapper,
                      index === filteredSubmissionRows.length - 1 && styles.lastTableRow,
                    ]}>
                    <Pressable
                      onPress={() =>
                        setExpandedSubmissionId((currentId) => (currentId === submission.id ? null : submission.id))
                      }
                      style={({ hovered }) => [
                        styles.tableRow,
                        hovered && styles.tableRowHovered,
                        isExpanded && styles.tableRowExpanded,
                      ]}>
                      <View style={[styles.tableCell, styles.assignmentColumn]}>
                        <View style={styles.assignmentCellContent}>
                          <View
                            style={[styles.assignmentIconContainer, { backgroundColor: typeColors.backgroundColor }]}>
                            {submission.submissionType === "Assessment" ? (
                              <ClipboardTextIcon size={18} color={typeColors.iconColor} weight="regular" />
                            ) : (
                              <UploadSimpleIcon size={18} color={typeColors.iconColor} weight="regular" />
                            )}
                          </View>

                          <View style={styles.assignmentTextContainer}>
                            <CustomText
                              text={submission.submissionTitle}
                              size="s"
                              weight="medium"
                              useTranslationText={false}
                              style={styles.assignmentTitle}
                            />
                            <CustomText
                              text={submission.submissionType}
                              size="xs"
                              useTranslationText={false}
                              style={styles.assignmentType}
                            />
                          </View>
                        </View>
                      </View>

                      <View style={[styles.tableCell, styles.moduleColumn]}>
                        <CustomText
                          text={submission.moduleName}
                          size="s"
                          useTranslationText={false}
                          style={styles.moduleText}
                        />
                      </View>

                      <View style={[styles.tableCell, styles.submittedColumn]}>
                        <View style={styles.submittedCellContent}>
                          <CustomText
                            text={submittedDateText}
                            size="s"
                            useTranslationText={false}
                            style={submission.status === "Missing" ? styles.missingText : styles.submittedText}
                          />
                          {row.isLate ? (
                            <CustomText text="Late" size="xs" useTranslationText={false} style={styles.lateText} />
                          ) : null}
                        </View>
                      </View>

                      <View style={[styles.tableCell, styles.statusColumn]}>
                        <View style={styles.statusPillWrapper}>
                          <Pill
                            text={row.normalizedStatus}
                            backgroundColor={statusPillColors.backgroundColor}
                            borderColor={statusPillColors.borderColor}
                            borderWidth={1}
                            textColor={statusPillColors.textColor}
                            textSize="xs"
                          />
                        </View>
                      </View>

                      <View style={[styles.tableCell, styles.gradeColumn]}>
                        {hasScore ? (
                          <View style={styles.gradeCellContent}>
                            <CustomText
                              text={`${row.scorePercentage}%`}
                              size="s"
                              weight="bold"
                              useTranslationText={false}
                              style={{ color: getValueColor(row.scorePercentage, colors.grey[700]) }}
                            />
                            <CustomText
                              text={`(${getDisplayNumber(submission.grade ?? null)}/${getDisplayNumber(
                                submission.maximumPoints ?? null
                              )})`}
                              size="xs"
                              useTranslationText={false}
                              style={styles.gradeFraction}
                            />
                          </View>
                        ) : (
                          <CustomText text="-" size="s" useTranslationText={false} style={styles.placeholderText} />
                        )}
                      </View>

                      <View style={[styles.tableCell, styles.attemptsColumn]}>
                        <CustomText
                          text={`${row.submissionAttempt}/${row.maxAttempts}`}
                          size="s"
                          useTranslationText={false}
                          style={styles.attemptsText}
                        />
                      </View>

                      <View style={[styles.tableCell, styles.expandColumn]}>
                        {isExpanded ? (
                          <CaretUpIcon size={16} color={colors.grey[600]} />
                        ) : (
                          <CaretDownIcon size={16} color={colors.grey[600]} />
                        )}
                      </View>
                    </Pressable>

                    {isExpanded ? (
                      <View style={styles.expandedPanel}>
                        <View style={styles.expandedMetaRow}>
                          {durationText !== "N/A" ? (
                            <View style={styles.metaItem}>
                              <ClockIcon size={14} color={colors.grey[500]} />
                              <CustomText
                                text={`Time spent: ${durationText}`}
                                size="xs"
                                weight="medium"
                                useTranslationText={false}
                                style={styles.metaPrimaryText}
                              />
                            </View>
                          ) : null}

                          {row.isLate && submission.dueDate ? (
                            <View style={styles.metaItem}>
                              <WarningCircleIcon size={14} color={colors.error[600]} />
                              <CustomText
                                text={`Submitted after due date (${dueDateText})`}
                                size="xs"
                                useTranslationText={false}
                                style={styles.metaErrorText}
                              />
                            </View>
                          ) : null}
                        </View>

                        {hasScore ? (
                          <View style={styles.scoreDetailRow}>
                            <View style={styles.scoreDetailBar}>
                              <CustomProgressBar
                                progress={row.scorePercentage}
                                color={
                                  row.scorePercentage != null && row.scorePercentage < 70
                                    ? colors.warning[500]
                                    : colors.success[500]
                                }
                                unfilledColor={colors.grey[200]}
                                ariaLabel={`${submission.submissionTitle} grade`}
                              />
                            </View>
                            <CustomText
                              text={row.scorePercentage != null ? `${row.scorePercentage}%` : "—"}
                              size="s"
                              weight="bold"
                              useTranslationText={false}
                              style={{ color: getValueColor(row.scorePercentage, colors.grey[700]) }}
                            />
                          </View>
                        ) : null}

                        {submission.submittedOn ? (
                          <View style={styles.expandedActionsRow}>
                            <CustomButton
                              text="View Submission"
                              useTranslationText={false}
                              size="s"
                              styleType="lightStroke"
                              leftIcon={<ArrowSquareOutIcon size={14} color={colors.grey[700]} />}
                              onPress={() => onViewSubmission(submission.id)}
                              textStyle={styles.viewSubmissionButtonText}
                              style={styles.viewSubmissionButton}
                            />
                          </View>
                        ) : null}
                      </View>
                    ) : null}
                  </View>
                );
              })
            )}
          </View>
        </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  submissionsCard: {
    backgroundColor: colors.white,
    borderRadius: radii.xl,
    borderWidth: 1,
    borderColor: colors.grey[200],
    overflow: "visible",
  },
  submissionsHeader: {
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    flexDirection: "row",
    justifyContent: "space-between",
    gap: spacing.md,
    minHeight: 60,
    paddingHorizontal: spacing.lg,
    paddingVertical: 0,
    zIndex: 10,
  },
  cardTitle: {
    color: colors.grey[900],
  },
  submissionsFilters: {
    alignItems: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.sm,
  },
  tableViewport: {
    backgroundColor: colors.white,
    borderBottomLeftRadius: radii.xl,
    borderBottomRightRadius: radii.xl,
    overflow: "hidden",
    width: "100%",
  },
  tableScroll: {
    width: "100%",
  },
  tableScrollContent: {
    flexGrow: 1,
  },
  table: {
    backgroundColor: colors.white,
    minWidth: 1080,
    width: "100%",
  },
  tableHeader: {
    alignItems: "center",
    backgroundColor: colors.grey[50],
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    flexDirection: "row",
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
  },
  headerText: {
    color: colors.grey[600],
  },
  tableCell: {
    justifyContent: "center",
    paddingHorizontal: spacing.xs,
  },
  assignmentColumn: {
    flex: 3.5,
  },
  moduleColumn: {
    flex: 2.4,
  },
  submittedColumn: {
    flex: 1.7,
  },
  statusColumn: {
    flex: 1.6,
  },
  gradeColumn: {
    flex: 1.3,
  },
  attemptsColumn: {
    flex: 0.9,
  },
  expandColumn: {
    alignItems: "flex-end",
    flex: 0.4,
  },
  tableRowWrapper: {
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  lastTableRow: {
    borderBottomWidth: 0,
  },
  tableRow: {
    alignItems: "center",
    backgroundColor: colors.white,
    flexDirection: "row",
    minHeight: 64,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
  },
  tableRowHovered: {
    backgroundColor: colors.grey[25],
  },
  tableRowExpanded: {
    backgroundColor: colors.grey[50],
  },
  statusPillWrapper: {
    alignSelf: "flex-start",
  },
  assignmentCellContent: {
    alignItems: "center",
    flexDirection: "row",
    gap: spacing.sm,
  },
  assignmentIconContainer: {
    alignItems: "center",
    borderRadius: radii.full,
    height: 32,
    justifyContent: "center",
    width: 32,
  },
  assignmentTextContainer: {
    flex: 1,
    gap: 2,
  },
  assignmentTitle: {
    color: colors.grey[900],
  },
  assignmentType: {
    color: colors.grey[600],
  },
  moduleText: {
    color: colors.grey[700],
  },
  submittedCellContent: {
    alignItems: "flex-start",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
  },
  submittedText: {
    color: colors.grey[900],
  },
  missingText: {
    color: colors.error[600],
  },
  lateText: {
    color: colors.error[600],
  },
  gradeCellContent: {
    alignItems: "flex-start",
    gap: 2,
  },
  gradeFraction: {
    color: colors.grey[600],
  },
  placeholderText: {
    color: colors.grey[500],
  },
  attemptsText: {
    color: colors.grey[700],
  },
  expandedPanel: {
    backgroundColor: colors.white,
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.lg,
    paddingTop: spacing.xs,
  },
  expandedMetaRow: {
    alignItems: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.md,
  },
  metaItem: {
    alignItems: "center",
    flexDirection: "row",
    gap: 6,
  },
  metaPrimaryText: {
    color: colors.grey[700],
  },
  metaErrorText: {
    color: colors.error[600],
  },
  scoreDetailRow: {
    alignItems: "center",
    flexDirection: "row",
    gap: spacing.md,
    marginTop: spacing.md,
  },
  scoreDetailBar: {
    flex: 1,
    maxWidth: 700,
  },
  expandedActionsRow: {
    marginTop: spacing.lg,
  },
  viewSubmissionButton: {
    alignSelf: "flex-start",
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  viewSubmissionButtonText: {
    color: colors.grey[700],
  },
  emptyState: {
    alignItems: "center",
    justifyContent: "center",
    minWidth: "100%",
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.xl,
  },
  emptyStateText: {
    color: colors.grey[600],
  },
});

export default RosterInsightSubmissionsTable;
